import {
  ADD_PROMPTS,
  ADD_QUERIES,
  ADD_CONVERSATION,
  SET_ACTIVE_CONVERSATION,
  FETCHING_PROMPTS,
  FETCHED_PROMPTS,
  FETCHING_QUERIES,
  FETCHED_QUERIES,
  CLEAR_ALL,
} from '../constants';

interface PromptsState {
  list: Record<string, any[]>;
  fetched: Record<string, boolean>;
  fetching: boolean;
  queries: {
    // keyed by project_id (string) → array of query records
    list: Record<string, any[]>;
    fetched: Record<string, boolean>;
    fetching: boolean;
  };
  activeConversationId: string | null;
}

const initialState: PromptsState = {
  list: {},
  fetched: {},
  fetching: false,
  queries: {
    list: {},
    fetched: {},
    fetching: false,
  },
  activeConversationId: null,
};

const promptsReducer = (state = initialState, { type, payload }: { type: string; payload?: any }): PromptsState => {
  switch (type) {
    case FETCHING_PROMPTS:
      return { ...state, fetching: true };
    case FETCHED_PROMPTS:
      return { ...state, fetching: false };
    case FETCHING_QUERIES:
      return { ...state, queries: { ...state.queries, fetching: true } };
    case FETCHED_QUERIES:
      return { ...state, queries: { ...state.queries, fetching: false } };
    case ADD_PROMPTS:
      return {
        ...state,
        list: { ...state.list, [payload.project_id]: payload.prompts },
        fetched: { ...state.fetched, [payload.project_id]: true },
        fetching: false,
      };
    case ADD_QUERIES: {
      const projectId = String(payload.project_id);
      const incoming: any[] = payload.queries || [];
      const existing: any[] = state.queries.list[projectId] || [];

      // Smart merge: prefer full records over shallow ones
      const merged = incoming.map((q: any) => {
        const prev = existing.find((e) => e.id === q.id);
        if (prev && prev.is_shallow === false) return prev;
        return q;
      });
      // Keep any existing full records not in the new list
      const mergedIds = new Set(merged.map((q: any) => q.id));
      const extras = existing.filter((e) => !mergedIds.has(e.id) && e.is_shallow === false);

      return {
        ...state,
        queries: {
          ...state.queries,
          list: { ...state.queries.list, [projectId]: [...merged, ...extras] },
          fetched: { ...state.queries.fetched, [projectId]: true },
          fetching: false,
        },
      };
    }
    case ADD_CONVERSATION: {
      // Merge full conversation records into the existing project query list
      const fullQueries: any[] = payload.queries || [];
      if (!fullQueries.length) return state;
      const projectId = String(payload.project_id || fullQueries[0]?.project_id);
      const existing: any[] = state.queries.list[projectId] || [];
      const fullIds = new Set(fullQueries.map((q: any) => q.id));

      const updated = existing.map((q: any) => {
        const full = fullQueries.find((f) => f.id === q.id);
        return full || q;
      });
      // Append any new turns that aren't yet in the list
      const newTurns = fullQueries.filter((q: any) => !existing.some((e) => e.id === q.id));

      return {
        ...state,
        queries: {
          ...state.queries,
          list: { ...state.queries.list, [projectId]: [...updated, ...newTurns] },
        },
      };
    }
    case SET_ACTIVE_CONVERSATION:
      return { ...state, activeConversationId: payload };
    case CLEAR_ALL:
      return { ...initialState };
    default:
      return state;
  }
};

export default promptsReducer;
